import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  Box,
  IconButton,
  Typography,
  Toolbar,
  Paper,
  CircularProgress,
  Alert,
  Slider,
  ButtonGroup,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ZoomOutMap as FitToWidthIcon,
  AspectRatio as FitToPageIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as ExitFullscreenIcon,
  Download as DownloadIcon,
  ViewList as ThumbnailsIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure PDF.js worker - use self-hosted for development reliability
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PDFViewerProps {
  url: string;
  title?: string;
  onDownload?: () => void;
}

type FitMode = 'width' | 'page' | 'custom';

export const PDFViewer: React.FC<PDFViewerProps> = ({ url, title, onDownload }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Create authenticated PDF file object
  const [pdfFile, setPdfFile] = useState<string | null>(null);
  
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [fitMode, setFitMode] = useState<FitMode>('width');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [thumbnailsOpen, setThumbnailsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Auto-detect container width for responsive scaling
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Fetch PDF with authentication
  useEffect(() => {
    let currentPdfUrl: string | null = null;

    const fetchAuthenticatedPDF = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required to view PDF');
          setLoading(false);
          return;
        }

        console.log('Fetching PDF from:', url);
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const blob = await response.blob();
          currentPdfUrl = URL.createObjectURL(blob);
          setPdfFile(currentPdfUrl);
          console.log('PDF loaded successfully');
        } else {
          console.error('PDF fetch failed:', response.status, response.statusText);
          setError(`Failed to load PDF: ${response.status} ${response.statusText}`);
        }
      } catch (err) {
        console.error('PDF fetch error:', err);
        setError('Network error while loading PDF');
      } finally {
        setLoading(false);
      }
    };

    if (url) {
      fetchAuthenticatedPDF();
    }

    return () => {
      // Cleanup blob URL when component unmounts or URL changes
      if (currentPdfUrl) {
        URL.revokeObjectURL(currentPdfUrl);
      }
    };
  }, [url]);

  // Cleanup pdfFile when component unmounts
  useEffect(() => {
    return () => {
      if (pdfFile) {
        URL.revokeObjectURL(pdfFile);
      }
    };
  }, [pdfFile]);

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.clientWidth;
        setContainerWidth(width);
        
        // Auto-adjust scale based on container width when in fit-to-width mode
        if (fitMode === 'width') {
          const newScale = (width - 40) / 595; // 595 is standard PDF page width in points
          setScale(Math.max(0.5, Math.min(3.0, newScale)));
        }
      }
    };

    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    return () => window.removeEventListener('resize', updateContainerWidth);
  }, [fitMode]);

  // Fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error);
    setError('Fehler beim Laden der PDF-Datei');
    setLoading(false);
  }, []);

  // Navigation functions
  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(numPages, prev + 1));
  }, [numPages]);

  const goToPage = useCallback((page: number) => {
    setPageNumber(Math.max(1, Math.min(numPages, page)));
  }, [numPages]);

  // Zoom functions
  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(3.0, prev + 0.25));
    setFitMode('custom');
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(0.5, prev - 0.25));
    setFitMode('custom');
  }, []);

  const fitToWidth = useCallback(() => {
    setFitMode('width');
    const newScale = (containerWidth - 40) / 595;
    setScale(Math.max(0.5, Math.min(3.0, newScale)));
  }, [containerWidth]);

  const fitToPage = useCallback(() => {
    setFitMode('page');
    const newScale = Math.min(
      (containerWidth - 40) / 595,
      (window.innerHeight - 200) / 842 // 842 is standard PDF page height in points
    );
    setScale(Math.max(0.5, Math.min(3.0, newScale)));
  }, [containerWidth]);

  // Fullscreen functions
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target !== document.body) return;

      switch (event.key) {
        case 'ArrowLeft':
        case 'PageUp':
          event.preventDefault();
          goToPrevPage();
          break;
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          event.preventDefault();
          goToNextPage();
          break;
        case 'Home':
          event.preventDefault();
          goToPage(1);
          break;
        case 'End':
          event.preventDefault();
          goToPage(numPages);
          break;
        case 'f':
        case 'F11':
          event.preventDefault();
          toggleFullscreen();
          break;
        case '+':
        case '=':
          event.preventDefault();
          zoomIn();
          break;
        case '-':
          event.preventDefault();
          zoomOut();
          break;
        case '0':
          event.preventDefault();
          fitToWidth();
          break;
        case '9':
          event.preventDefault();
          fitToPage();
          break;
        case 'Escape':
          if (thumbnailsOpen) {
            setThumbnailsOpen(false);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevPage, goToNextPage, goToPage, numPages, toggleFullscreen, zoomIn, zoomOut, fitToWidth, fitToPage, thumbnailsOpen]);

  const renderThumbnails = () => (
    <Drawer
      anchor="left"
      open={thumbnailsOpen}
      onClose={() => setThumbnailsOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Seitenübersicht</Typography>
          <IconButton onClick={() => setThumbnailsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <List disablePadding>
          {Array.from({ length: numPages }, (_, index) => {
            const pageNum = index + 1;
            return (
              <ListItem key={pageNum} disablePadding>
                <ListItemButton
                  selected={pageNum === pageNumber}
                  onClick={() => {
                    goToPage(pageNum);
                    setThumbnailsOpen(false);
                  }}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      border: pageNum === pageNumber ? '2px solid' : '1px solid',
                      borderColor: pageNum === pageNumber ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      mb: 1,
                    }}
                  >
                    <Document file={pdfFile} loading="">
                      <Page
                        pageNumber={pageNum}
                        width={240}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </Box>
                  <ListItemText
                    primary={`Seite ${pageNum}`}
                    sx={{ textAlign: 'center', m: 0 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: isFullscreen ? '100vh' : '80vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.100',
        position: 'relative',
      }}
    >
      {/* Toolbar */}
      <Paper elevation={1} sx={{ zIndex: 2 }}>
        <Toolbar variant="dense" sx={{ minHeight: 56, gap: 1 }}>
          {/* Page Navigation */}
          <ButtonGroup size="small">
            <Tooltip title="Vorherige Seite (←)">
              <span>
                <IconButton
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                  size="small"
                >
                  <PrevIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Nächste Seite (→)">
              <span>
                <IconButton
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                  size="small"
                >
                  <NextIcon />
                </IconButton>
              </span>
            </Tooltip>
          </ButtonGroup>

          {/* Page Info */}
          <Typography variant="body2" sx={{ mx: 2, minWidth: 100 }}>
            {loading ? 'Laden...' : `${pageNumber} von ${numPages}`}
          </Typography>

          {/* Zoom Controls */}
          <ButtonGroup size="small">
            <Tooltip title="Verkleinern (-)">
              <IconButton onClick={zoomOut} disabled={scale <= 0.5}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vergrößern (+)">
              <IconButton onClick={zoomIn} disabled={scale >= 3.0}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="An Breite anpassen (0)">
              <IconButton onClick={fitToWidth}>
                <FitToWidthIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Ganze Seite (9)">
              <IconButton onClick={fitToPage}>
                <FitToPageIcon />
              </IconButton>
            </Tooltip>
          </ButtonGroup>

          {/* Zoom Slider */}
          <Box sx={{ width: 120, mx: 2 }}>
            <Slider
              value={scale}
              onChange={(_, value) => {
                setScale(value as number);
                setFitMode('custom');
              }}
              min={0.5}
              max={3.0}
              step={0.1}
              size="small"
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Action Buttons */}
          <ButtonGroup size="small">
            <Tooltip title="Seitenübersicht">
              <IconButton onClick={() => setThumbnailsOpen(true)}>
                <ThumbnailsIcon />
              </IconButton>
            </Tooltip>
            {onDownload && (
              <Tooltip title="Herunterladen">
                <IconButton onClick={onDownload}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={isFullscreen ? "Vollbild verlassen (F)" : "Vollbild (F)"}>
              <IconButton onClick={toggleFullscreen}>
                {isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </Toolbar>
      </Paper>

      {/* PDF Document */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 2,
          bgcolor: 'grey.100',
        }}
      >
        {loading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%' }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              PDF wird geladen...
            </Typography>
          </Box>
        )}

        <Document
          file={pdfFile}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
        >
          <Paper
            elevation={3}
            sx={{
              display: 'inline-block',
              p: 0,
              bgcolor: 'white',
            }}
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Paper>
        </Document>
      </Box>

      {/* Mobile Navigation FAB */}
      {isMobile && !isFullscreen && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Fab
            size="small"
            color="primary"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            <PrevIcon />
          </Fab>
          <Fab
            size="small"
            color="primary"
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
          >
            <NextIcon />
          </Fab>
        </Box>
      )}

      {/* Thumbnails Drawer */}
      {renderThumbnails()}
    </Box>
  );
};